import { type FC, type PropsWithChildren } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

import { useURLSearchParam } from "widgets/hooks";
import { toSafeInt } from "widgets/utils";

import { ContextWidgetType, type INavigationContextProps } from "../core/navigation-panel/ContextWidget.js";
import PageLayout from "../core/navigation-panel/PageLayout.js";
import { GenomeView, type IGenomeViewOwnProps } from "./GenomeView.js";
import { useGetRegionFlag } from "./flag-track/useFlagGraphql.js";
import { useDocumentTitle } from "@/app-utils/hooks/useDocumentTitle.ts";
import { useURLViewID } from "@/app-utils/hooks/useUrl.ts";
import { getPaddedRange } from "@/app-utils/range.js";
import { getClassicGenomeBrowserTracks } from "@/redux/modules/genome-browser.js";
import { type SavedViewState } from "@/redux/modules/saved-view.js";
import { type IAppState } from "@/redux/reducer.js";
import { TRACK_TYPE, UNKNOWN_GENOME_ID } from "@/settings/settings.js";

export const GenomeViewerPage: FC<PropsWithChildren> = () => {
  const viewId = useURLViewID();
  const flag = useURLSearchParam("flag");
  const { genomeId } = useParams<"genomeId">();
  const { view, isLoading: isSavedViewLoading } = useSelector<IAppState, SavedViewState>((state) => state.savedView);

  useDocumentTitle("Genome View");
  const { data: regionFlag } = useGetRegionFlag({ flagId: toSafeInt(flag) });

  let genomeViewProps: IGenomeViewOwnProps = {
    genomeId: undefined,
    defaultTracks: getClassicGenomeBrowserTracks([TRACK_TYPE.SEQUENCE, TRACK_TYPE.GENE]),
  };

  if (flag && regionFlag) {
    // Bootstrap genome browser parameters from flag
    genomeViewProps = {
      ...genomeViewProps,
      genomeId: regionFlag.region?.contig?.organism?.id,
      contigId: regionFlag.region?.contig?.id,
      range: getPaddedRange(regionFlag.region?.range, regionFlag.region?.contig?.length),
    };
  } else if (!flag) {
    // Bootstrap genome viewer in global mode with genome selection open
    genomeViewProps = {
      ...genomeViewProps,
      genomeId: toSafeInt(genomeId) || UNKNOWN_GENOME_ID,
    };
  }

  const getContextProps = (): INavigationContextProps | undefined => {
    if (viewId && !isSavedViewLoading && Number.parseInt(view?.id) === viewId) {
      return {
        type: ContextWidgetType.SAVED_VIEW,
        data: view,
      };
    }

    return undefined;
  };

  return (
    <PageLayout
      contextWidgetProps={getContextProps()}
      navPanelCategories={[
        {
          label: "Genome View",
          navItems: [
            {
              label: "Overview",
              route: ``,
              pageComponent: <GenomeView {...genomeViewProps} />,
            },
          ],
        },
      ]}
      navPanelCollapsed
    />
  );
};
